import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  accepterInvitations,
  annulerCourse,
  assignerCourse,
  avancerCourse,
  chargerTout,
  monProfil,
  creerCompte,
  seConnecter,
  seDeconnecter,
  sessionEnCours,
  suivreCourses,
  suivreLivreurs,
  suivreMessages,
  type MonProfil,
} from '../api/operations'
import { compterAlertes, type FixturesAlertes } from '../api/alertes'
import { suivreMessagesClients, totalNonLus, type FixturesMessagerie } from '../api/messagerieClients'
import { suivreBoitiers } from '../api/boitiers'
import { controlerEntree } from '../api/controleAcces'
import { erreurDeRetourOAuth, sessionOuverteParGoogle } from '../api/google'
import { peut } from '../api/roles'
import type { Course, DonneesOps, RoleMembre } from '../api/types'
import { Contexte } from './Contexte'
import { BarreLaterale } from './BarreLaterale'
import { Connexion } from './Connexion'
import { ECRANS, type IdEcran } from './Navigation'
import { useNotification } from './Notification'
import { EcranAlertes } from './ecrans/Alertes'
import { EcranBoitiers } from './ecrans/Boitiers'
import { EcranClients } from './ecrans/Clients'
import { EcranCompta } from './ecrans/Compta'
import { EcranDispatch } from './ecrans/Dispatch'
import { EcranEquipe } from './ecrans/Equipe'
import { EcranHistoriqueGps } from './ecrans/Historique'
import { EcranLivreurs } from './ecrans/Livreurs'
import { EcranMessagerie, type OngletMessagerie } from './ecrans/Messagerie'
import { EcranOnboardingPme } from './ecrans/OnboardingPme'
import { EcranPriseAppel } from './ecrans/PriseAppel'
import { EcranReglages } from './ecrans/Reglages'
import { EcranPaie } from './ecrans/Paie'

export function App() {
  const [orgId, setOrgId] = useState<string | null>(null)
  const [donnees, setDonnees] = useState<DonneesOps | null>(null)
  const [profil, setProfil] = useState<MonProfil | null>(null)
  const [ecran, setEcran] = useState<IdEcran>('dispatch')
  const [erreurConnexion, setErreurConnexion] = useState<string | null>(null)
  const [restauration, setRestauration] = useState(true)
  const [role, setRole] = useState<RoleMembre | null>(null)
  const [apercu, setApercu] = useState(false)
  /* Alertes GPS (0052) : alertes à traiter (pastille de la navigation) et — aperçu seulement — leurs données fictives. */
  const [nbAlertes, setNbAlertes] = useState(0)
  const [fixturesAlertes, setFixturesAlertes] = useState<FixturesAlertes | null>(null)
  const [fixturesMessagerie, setFixturesMessagerie] = useState<FixturesMessagerie | null>(null)
  /* Ouvrir la messagerie sur un client (et une course) depuis le détail d'une course. */
  const [cibleMessagerie, setCibleMessagerie] = useState<{ onglet: OngletMessagerie; client: string | null; course: string | null }>({ onglet: 'clients', client: null, course: null })
  /* Onboarding PME : dossier à ouvrir d'emblée (entrée depuis l'écran Clients). */
  const [clientOnboarding, setClientOnboarding] = useState<string | null>(null)
  const { notifier, bandeau } = useNotification()

  /* `recharger` est appelé depuis les abonnements temps réel. Sans cette
     référence, chaque rechargement recréerait les abonnements, qui
     rechargeraient à leur tour — une boucle. */
  const orgRef = useRef<string | null>(null)
  orgRef.current = orgId

  const apercuRef = useRef(false)
  apercuRef.current = apercu

  const recharger = useCallback(async () => {
    const id = orgRef.current
    if (!id || apercuRef.current) return
    try {
      setDonnees(await chargerTout(id))
    } catch (e) {
      notifier(`Erreur de chargement : ${e instanceof Error ? e.message : String(e)}`)
    }
  }, [notifier])

  /** Entrée dans la console une fois l'organisation résolue. */
  const entrer = useCallback(
    async (id: string) => {
      setOrgId(id)
      orgRef.current = id
      try {
        const [d, p] = await Promise.all([chargerTout(id), monProfil(id)])
        setDonnees(d)
        setProfil(p)
      } catch (e) {
        notifier(`Erreur de chargement : ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    [notifier],
  )

  /** Résout l'accès puis entre — ou refuse, avec le motif exact. */
  const resoudrePuisEntrer = useCallback(
    async (userId: string): Promise<boolean> => {
      /* AVANT de lire les adhésions : une invitation en attente (adressée à l'e-mail OU au numéro WhatsApp du compte)
         devient une adhésion active (0024, 0041). Un échec ici n'empêche pas d'entrer. */
      await accepterInvitations()
      const verdict = await controlerEntree(userId, await sessionOuverteParGoogle())
      if (!verdict.ok) {
        /* Déjà déconnecté par `controlerEntree` : le laisser en session le ferait buter sans fin sur des refus RLS muets. */
        setErreurConnexion(verdict.message)
        return false
      }
      const acces = verdict.acces
      setErreurConnexion(null)
      setRole(acces.role)
      /* Le comptable n'a rien à faire à la prise d'appel : il arrive sur la compta. */
      setEcran(acces.role === 'accountant' ? 'compta' : 'dispatch')
      await entrer(acces.orgId)
      return true
    },
    [entrer],
  )

  /* Restauration de session au chargement — y compris au RETOUR de Google :
     `getSession()` attend l'échange du code PKCE, puis le même contrôle
     d'accès qu'après un mot de passe s'applique. */
  useEffect(() => {
    void (async () => {
      /* Aperçu de DÉVELOPPEMENT (`?apercu`, `?apercu=accountant`…) : données
         fictives, aucune connexion. `import.meta.env.DEV` vaut `false` au
         build : ce bloc et le module importé disparaissent de la production. */
      if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('apercu')) {
        const { alertesApercu, donneesApercu, messagerieApercu, roleApercu } = await import('./apercu')
        const r = roleApercu(new URLSearchParams(window.location.search).get('apercu'))
        setApercu(true)
        setRole(r)
        setEcran(r === 'accountant' ? 'compta' : 'dispatch')
        setOrgId('apercu')
        orgRef.current = 'apercu'
        setDonnees(donneesApercu())
        const fa = alertesApercu()
        setFixturesAlertes(fa)
        setFixturesMessagerie(messagerieApercu())
        setNbAlertes(fa.compte.aTraiter)
        setProfil({ nom: 'Aperçu Développement', email: 'apercu@exemple.invalid', telephone: '', role: r, titre: 'Données fictives' })
        setRestauration(false)
        return
      }
      const retour = erreurDeRetourOAuth()
      if (retour) setErreurConnexion(retour)
      try {
        const userId = await sessionEnCours()
        if (userId) await resoudrePuisEntrer(userId)
      } catch {
        /* Session illisible : on retombe simplement sur l'écran de connexion. */
      } finally {
        setRestauration(false)
      }
    })()
  }, [resoudrePuisEntrer])

  /* Temps réel : courses et messagerie. Les abonnements suivent l'organisation
     et sont démontés à la déconnexion — sinon ils continueraient d'écrire dans
     une console qu'on a quittée. */
  useEffect(() => {
    if (!orgId || apercu) return
    const fermerCourses = suivreCourses(orgId, () => void recharger())
    const fermerMessages = suivreMessages(orgId, () => void recharger())
    /* Messagerie clients (0067) : un message de client ou de fil de course recharge l'état (pastille, listes). */
    const fermerMessagesClients = suivreMessagesClients(orgId, () => void recharger())
    /* Positions GPS : on remplace la seule fiche du livreur, sans tout
       recharger à chaque point (un livreur en émet toutes les quelques
       secondes). */
    const fermerLivreurs = suivreLivreurs(orgId, (l) =>
      setDonnees((d) => {
        if (!d) return d
        const existe = d.drivers.some((x) => x.id === l.id)
        return { ...d, drivers: existe ? d.drivers.map((x) => (x.id === l.id ? l : x)) : [...d.drivers, l] }
      }),
    )
    /* Boîtiers GPS (0026) : même principe, une fiche remplacée à la fois. */
    const fermerBoitiers = suivreBoitiers(
      orgId,
      (b) =>
        setDonnees((d) => {
          if (!d) return d
          const existe = d.boitiers.some((x) => x.id === b.id)
          return { ...d, boitiers: existe ? d.boitiers.map((x) => (x.id === b.id ? b : x)) : [...d.boitiers, b] }
        }),
      (id) => setDonnees((d) => (d ? { ...d, boitiers: d.boitiers.filter((x) => x.id !== id) } : d)),
    )
    return () => {
      fermerCourses()
      fermerMessages()
      fermerMessagesClients()
      fermerLivreurs()
      fermerBoitiers()
    }
  }, [orgId, recharger, apercu])

  /* La cloche : alertes GPS à traiter. Les règles sont évaluées par la base ; la console interroge un compteur léger toutes les 30 s
     (et dès qu'un acquittement a lieu). Si l'appel échoue (migration 0052 absente, réseau), on ne touche à rien : la pastille garde
     sa dernière valeur plutôt que de mentir par un 0 ou de faire du bruit. En aperçu : les données fictives, jamais d'appel. */
  const rafraichirAlertes = useCallback(() => {
    const id = orgRef.current
    if (!id || apercuRef.current) return
    void compterAlertes(id).then((c) => {
      if (c && orgRef.current === id) setNbAlertes(c.aTraiter)
    })
  }, [])
  const voitAlertes = peut(role, 'alertes_voir')
  useEffect(() => {
    if (!orgId || apercu || !voitAlertes) return
    rafraichirAlertes()
    const t = setInterval(rafraichirAlertes, 30_000)
    return () => clearInterval(t)
  }, [orgId, apercu, voitAlertes, rafraichirAlertes])

  const connexion = useCallback(
    async (email: string, motDePasse: string) => {
      try {
        const userId = await seConnecter(email, motDePasse)
        await resoudrePuisEntrer(userId)
      } catch (e) {
        setErreurConnexion(e instanceof Error ? e.message : String(e))
      }
    },
    [resoudrePuisEntrer],
  )

  /* Connexion par numéro + code WhatsApp : une fois le code vérifié, EXACTEMENT le même chemin qu'après un mot de
     passe (invitations, rôle, organisation opératrice, route d'entrée). */
  const connexionWhatsapp = useCallback(
    async (userId: string) => {
      setErreurConnexion(null)
      try {
        await resoudrePuisEntrer(userId)
      } catch (e) {
        setErreurConnexion(e instanceof Error ? e.message : String(e))
      }
    },
    [resoudrePuisEntrer],
  )

  /* Création de compte : n'accorde AUCUN droit. Si une session s'ouvre (confirmation
     e-mail désactivée), le contrôle d'accès habituel refuse le compte sans rôle. */
  const inscription = useCallback(
    async (nom: string, email: string, motDePasse: string) => {
      setErreurConnexion(null)
      const r = await creerCompte(nom, email, motDePasse)
      if (r.sessionOuverte) {
        const userId = await sessionEnCours()
        if (userId) await resoudrePuisEntrer(userId)
      }
      return r
    },
    [resoudrePuisEntrer],
  )

  const deconnexion = useCallback(async () => {
    await seDeconnecter()
    setOrgId(null)
    orgRef.current = null
    setDonnees(null)
    setProfil(null)
    setRole(null)
    setNbAlertes(0)
    setEcran('dispatch')
  }, [])

  /* ------------------------------------------------------------------ */
  /*  Actions sur les courses                                             */
  /* ------------------------------------------------------------------ */

  /* Chacune recharge APRÈS coup : l'edge function recalcule aussi le statut
     du livreur, qu'un rafraîchissement local ne saurait pas deviner. */
  const affecter = useCallback(
    async (courseId: string, livreurId: string) => {
      if (!orgRef.current) return
      if (!livreurId) {
        notifier('Choisissez un livreur')
        return
      }
      try {
        await assignerCourse(orgRef.current, courseId, livreurId)
        const d = donnees?.drivers.find((x) => x.id === livreurId)
        notifier(`Assignée à ${d?.full_name ?? 'livreur'}`)
        await recharger()
      } catch (e) {
        notifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    [donnees, notifier, recharger],
  )

  const avancer = useCallback(
    async (course: Course) => {
      if (!orgRef.current) return
      try {
        await avancerCourse(orgRef.current, course)
        await recharger()
      } catch (e) {
        notifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    [notifier, recharger],
  )

  const annuler = useCallback(
    async (courseId: string) => {
      if (!orgRef.current) return
      try {
        await annulerCourse(orgRef.current, courseId)
        notifier('Course annulée')
        await recharger()
      } catch (e) {
        notifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
      }
    },
    [notifier, recharger],
  )

  /* Les deux pastilles de la navigation. */
  const enAttente = useMemo(
    () => donnees?.courses.filter((c) => c.statut === 'creee').length ?? 0,
    [donnees],
  )
  const nonLus = useMemo(
    () =>
      (donnees?.messages.filter((m) => m.sender === 'driver' && !m.read_at).length ?? 0) +
      totalNonLus(donnees?.conversationsClients ?? []),
    [donnees],
  )

  if (restauration) return null
  if (!orgId) return <Connexion surConnexion={connexion} surConnexionWhatsapp={connexionWhatsapp} surInscription={inscription} erreur={erreurConnexion} surErreur={setErreurConnexion} />

  /* Écrans ouverts au rôle : la comptabilité demande les factures (lisibles
     owner/comptable seulement), la messagerie les fils (owner/dispatch). */
  const visibles = ECRANS.filter(
    (e) =>
      (e.id !== 'compta' || peut(role, 'comptabilite')) &&
      (e.id !== 'messagerie' || peut(role, 'messagerie')) &&
      (e.id !== 'reglages' || peut(role, 'tarifs')) &&
      (e.id !== 'paie' || peut(role, 'paie_livreurs')) &&
      (e.id !== 'onboarding' || peut(role, 'onboarding_voir')) &&
      (e.id !== 'boitiers' || peut(role, 'boitiers_voir')) &&
      (e.id !== 'historique' || peut(role, 'boitiers_voir')) &&
      (e.id !== 'alertes' || peut(role, 'alertes_voir')),
  )
  const courant = visibles.find((e) => e.id === ecran) ?? visibles[0] ?? ECRANS[0]

  return (
    <Contexte.Provider value={{ role, apercu, fixturesAlertes, fixturesMessagerie }}>
      {apercu && <div className="badge-dev">Aperçu DEV — données fictives</div>}
      <div id="app" className="on">
        <BarreLaterale
          actif={courant.id}
          ecrans={visibles}
          surChangement={(id) => { if (id !== 'onboarding') setClientOnboarding(null); setEcran(id) }}
          enAttente={enAttente}
          nonLus={nonLus}
          alertes={nbAlertes}
          profil={profil}
          nomOrg={donnees?.org.name ?? 'SwiftDeliver Opérations'}
          surDeconnexion={() => void deconnexion()}
        />

        <main className="content">
          <div className="topbar">
            <div>
              <h1 id="page-title">{courant.libelle}</h1>
              <div className="tb-sub" id="page-sub">{courant.sous}</div>
            </div>
            <div className="tb-spacer" />
            <div className="tb-live">
              <span className="dot" /> <span id="live-label">{apercu ? 'Aperçu hors ligne' : 'Exploitation en direct'}</span>
            </div>
          </div>

          <div className="wrap">
            {!donnees ? (
              <p style={{ padding: 18, color: 'var(--muted)' }}>Chargement…</p>
            ) : (
              <section className="screen active" id={`s-${courant.id}`}>
                {courant.id === 'call' ? (
                  <EcranPriseAppel
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                    surAllerDispatch={() => setEcran('dispatch')}
                    surAssigner={(c, l) => void affecter(c, l)}
                    surAvancer={(c) => void avancer(c)}
                    surAnnuler={(c) => void annuler(c)}
                  />
                ) : courant.id === 'dispatch' ? (
                  <EcranDispatch
                    donnees={donnees}
                    surNouvelleCourse={() => setEcran('call')}
                    {...(peut(role, 'messagerie')
                      ? {
                          surEcrireClient: (client: string, course: string | null) => {
                            setCibleMessagerie({ onglet: 'clients', client, course })
                            setEcran('messagerie')
                          },
                        }
                      : {})}
                    surAssigner={(c, l) => void affecter(c, l)}
                    surAvancer={(c) => void avancer(c)}
                    surAnnuler={(c) => void annuler(c)}
                    surNotifier={notifier}
                    surRafraichir={recharger}
                  />
                ) : courant.id === 'clients' ? (
                  <EcranClients
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                    surOuvrirOnboarding={(id) => { setClientOnboarding(id); setEcran('onboarding') }}
                  />
                ) : courant.id === 'onboarding' ? (
                  <EcranOnboardingPme
                    orgId={orgId}
                    clientInitial={clientOnboarding}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                  />
                ) : courant.id === 'drivers' ? (
                  <EcranLivreurs
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                  />
                ) : courant.id === 'boitiers' ? (
                  <EcranBoitiers
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                  />
                ) : courant.id === 'historique' ? (
                  <EcranHistoriqueGps donnees={donnees} orgId={orgId} surAllerAlertes={peut(role, 'alertes_voir') ? () => setEcran('alertes') : undefined} />
                ) : courant.id === 'alertes' ? (
                  <EcranAlertes donnees={donnees} orgId={orgId} surNotifier={notifier} surAcquittement={rafraichirAlertes} />
                ) : courant.id === 'messagerie' ? (
                  <EcranMessagerie
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                    ongletInitial={cibleMessagerie.onglet}
                    clientInitial={cibleMessagerie.client}
                    courseInitiale={cibleMessagerie.course}
                  />
                ) : courant.id === 'paie' ? (
                  <EcranPaie orgId={orgId} surRafraichir={recharger} surNotifier={notifier} />
                ) : courant.id === 'reglages' ? (
                  <EcranReglages
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                  />
                ) : courant.id === 'equipe' ? (
                  <EcranEquipe
                    donnees={donnees}
                    orgId={orgId}
                    surRafraichir={recharger}
                    surNotifier={notifier}
                  />
                ) : (
                  <EcranCompta donnees={donnees} orgId={orgId} surNotifier={notifier} surRafraichir={recharger} />
                )}
              </section>
            )}
          </div>
        </main>
      </div>
      {bandeau}
    </Contexte.Provider>
  )
}
